// RoomsReport — inventory + utilisation snapshot.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReportShell from "./ReportShell";
import { Panel, Loading, fmtMoney } from "./OverviewReport";
import { StatTile, PieChart, BarChart } from "../../../components/charts";
import { roomsUrl, roomTypesUrl } from "../../../BaseURL";

const STATUS_COLORS = {
  available:    "#4a7c59",
  occupied:     "#a93226",
  reserved:     "#3a7bd5",
  cleaning:     "#c8a05a",
  maintenance:  "#e67e22",
  out_of_order: "#7a4a4a",
};

export default function RoomsReport({ range, onRangeChange }) {
  const [rooms, setRooms] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [r, t] = await Promise.all([axios.get(roomsUrl), axios.get(roomTypesUrl)]);
        if (cancelled) return;
        setRooms(r.data?.results || []);
        setTypes(t.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    const active = rooms.filter(r => r.is_active === 1);
    const byStatus = Object.keys(STATUS_COLORS).map(s => ({
      label: s.replace("_", " "), value: active.filter(r => r.status === s).length, color: STATUS_COLORS[s],
    })).filter(s => s.value > 0);

    const byType = aggregate(active, r => r.room_type_name || "Untyped");
    const occupiedPct = active.length > 0 ? Math.round((active.filter(r => r.status === "occupied").length / active.length) * 100) : 0;
    const blockedCount = active.filter(r => ["maintenance","out_of_order"].includes(r.status)).length;

    return { active, byStatus, byType, occupiedPct, blockedCount };
  }, [rooms]);

  const csv = useMemo(() => ({
    headers: ["Room #","Type","Floor","Status","Nightly","Hourly","Daily","Bed","View","Max Occ","Amenities","Active"],
    rows: rooms.map(r => {
      const type = types.find(t => t.id === r.room_type_id);
      return [
        r.room_number, r.room_type_name || "", r.floor || "", r.status,
        Number(r.price_per_night).toFixed(2),
        type?.hourly_rate != null ? Number(type.hourly_rate).toFixed(2) : "",
        type?.daily_rate  != null ? Number(type.daily_rate ).toFixed(2) : "",
        r.bed_type || "", r.view_type || "", r.max_occupancy || "",
        r.amenities || "", r.is_active === 1 ? "yes" : "no",
      ];
    }),
  }), [rooms, types]);

  return (
    <ReportShell
      title="Rooms Report"
      subtitle="Inventory, status and rate snapshot"
      range={{ ...range, onChange: onRangeChange }}
      csv={csv}
      filename="rooms"
    >
      {loading ? <Loading/> : (
        <>
          <div className="kpi-row">
            <StatTile icon="🏨" label="Active Rooms" value={stats.active.length}
              sub={`${rooms.length - stats.active.length} inactive`} />
            <StatTile icon="📊" label="Occupancy"     value={`${stats.occupiedPct}%`} accent="#3a7bd5" />
            <StatTile icon="🧹" label="Cleaning"      value={stats.byStatus.find(s => s.label === "cleaning")?.value || 0} accent="#c8a05a" />
            <StatTile icon="⛔" label="Blocked"       value={stats.blockedCount}
              sub="maintenance / out of order" accent="#a93226" />
            <StatTile icon="🏷️" label="Room Types"   value={types.length} accent="#8a6a30" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 16 }}>
            <Panel title="Status Breakdown"><PieChart data={stats.byStatus}/></Panel>
            <Panel title="By Type">          <BarChart data={stats.byType}/></Panel>
          </div>

          <Panel title="All Rooms" style={{ marginTop: 18 }}>
            <table className="tbl" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Room #</th><th>Type</th><th>Floor</th><th>Status</th>
                  <th style={{ textAlign: "right" }}>Nightly</th>
                  <th>Bed</th><th>View</th><th style={{ textAlign: "center" }}>Sleeps</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map(r => (
                  <tr key={r.id} style={{ opacity: r.is_active ? 1 : .55 }}>
                    <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16 }}>{r.room_number}</td>
                    <td>{r.room_type_name || "—"}</td>
                    <td>{r.floor || "—"}</td>
                    <td>
                      <span style={{
                        display: "inline-block", padding: "3px 8px", borderRadius: 999,
                        background: `${STATUS_COLORS[r.status] || "#8a7060"}22`,
                        color: STATUS_COLORS[r.status] || "#8a7060",
                        fontSize: 10.5, fontWeight: 500, letterSpacing: ".08em", textTransform: "uppercase",
                      }}>{r.status.replace("_", " ")}</span>
                    </td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.price_per_night)}</td>
                    <td>{r.bed_type || "—"}</td>
                    <td>{r.view_type || "—"}</td>
                    <td style={{ textAlign: "center" }}>{r.max_occupancy || "—"}</td>
                  </tr>
                ))}
                {rooms.length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#8a7060", fontStyle: "italic" }}>
                    No rooms registered yet.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </ReportShell>
  );
}

function aggregate(list, keyFn) {
  const m = new Map();
  for (const r of list) {
    const k = keyFn(r) || "—";
    m.set(k, (m.get(k) || 0) + 1);
  }
  return Array.from(m.entries()).map(([label, value]) => ({ label, value }));
}
